<template>
  <component
    :is="clickable ? 'button' : 'div'"
    :type="clickable ? 'button' : undefined"
    class="kpi-card"
    :class="{ 'kpi-card--clickable': clickable, 'kpi-card--loading': loading }"
    :aria-label="ariaLabel"
    @click="onClick"
  >
    <div class="kpi-card-row">
      <div class="kpi-card-label">{{ label }}</div>
      <v-icon v-if="icon" class="kpi-card-icon">{{ icon }}</v-icon>
    </div>
    <div class="kpi-card-value">
      <span v-if="loading" class="kpi-card-skeleton" aria-hidden="true"></span>
      <span v-else>{{ formattedValue }}</span>
    </div>
    <div v-if="delta && !loading" class="kpi-card-delta">{{ delta }}</div>
  </component>
</template>

<script>
import { computed } from "vue";

export default {
  name: "KpiCard",
  props: {
    label: { type: String, required: true },
    value: { type: [Number, String], default: 0 },
    delta: { type: String, default: "" },
    icon: { type: String, default: "" },
    clickable: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
  emits: ["select"],
  setup(props, { emit }) {
    const formattedValue = computed(() => {
      const v = props.value;
      if (typeof v === "number" && Number.isFinite(v)) {
        // Integer counts get thousands separators; fractions get one decimal.
        if (Number.isInteger(v)) return v.toLocaleString();
        return (Math.round(v * 10) / 10).toLocaleString();
      }
      return v == null ? "-" : String(v);
    });

    const ariaLabel = computed(() => `${props.label}: ${formattedValue.value}`);

    const onClick = () => {
      if (!props.clickable || props.loading) return;
      emit("select");
    };

    return { formattedValue, ariaLabel, onClick };
  },
};
</script>

<style scoped>
.kpi-card {
  background: var(--lulu-surface);
  border-radius: var(--lulu-radius-card);
  box-shadow: var(--lulu-card-shadow);
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: 1px solid transparent;
  transition: transform 0.15s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  text-align: left;
  width: 100%;
  font: inherit;
  color: inherit;
}

.kpi-card--clickable {
  cursor: pointer;
}

.kpi-card--clickable:hover {
  transform: translateY(-2px);
  box-shadow: var(--lulu-card-shadow-hover);
  border-color: var(--lulu-primary-border);
}

.kpi-card--clickable:focus-visible {
  outline: 2px solid var(--lulu-primary);
  outline-offset: 2px;
}

.kpi-card-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.kpi-card-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--lulu-text-muted);
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.kpi-card-icon {
  color: var(--lulu-primary);
  font-size: 20px !important;
}

.kpi-card-value {
  font-size: 30px;
  font-weight: 800;
  color: var(--lulu-text-strong);
  letter-spacing: -0.5px;
  line-height: 1.1;
  min-height: 36px;
  display: flex;
  align-items: center;
}

.kpi-card-delta {
  font-size: 12px;
  color: var(--lulu-text-muted);
  font-weight: 500;
}

.kpi-card-skeleton {
  display: inline-block;
  width: 60%;
  height: 24px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    var(--lulu-surface-soft) 0%,
    #eef2f7 50%,
    var(--lulu-surface-soft) 100%
  );
  background-size: 200% 100%;
  animation: kpi-shimmer 1.4s linear infinite;
}

@keyframes kpi-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
